// 接続先設定。値は vite の define で `import.meta.env.VITE_*` に畳み込まれる（tst-x1mt）。
//
// 【既定値を置かない】以前ここには `?? STG_API` の形で stg の API と Cognito Client ID が
// 既定値として書かれていた。VITE_* はどのビルド経路からも注入されていなかったため、
// prod の配信もこの既定値、つまり stg の API を向いたままだった（DNS 開通後は実顧客の予約と
// 決済が stg の DB に入る。stg の CORS は "*" なのでエラーは一切出ない）。
// 既定値を残したまま注入だけ足すと、注入を忘れた次の人が同じ状態を再生産する。
//
// 値の出所は config/frontend-env.json（TST_ENV をキーにした表）。解決は config/frontend-env.ts。
// ここは「注入されていなければ即座に死ぬ」ことだけを担う最後の砦で、
// 本丸の検査はデプロイ側（web-stack の刻印照合・scripts/check-frontend-bundle-env.mjs）にある。
// guide-web は guide-app と同一の Cognito guide App Client（CUSTOM_AUTH = email + 6桁コード / C-3-2）を使う。
//
function requireInjected(name: string, value: string | undefined): string {
  if (value === undefined || value.trim() === "") {
    throw new Error(
      `${name} がビルド時に注入されていません。config/frontend-env.ts（vite の define）を経由せずに ` +
        `ビルドされた成果物です。TST_ENV を指定して pnpm build し直してください。`,
    );
  }
  return value;
}

export const config = {
  apiBaseUrl: requireInjected("VITE_API_BASE_URL", import.meta.env.VITE_API_BASE_URL),
  cognito: {
    region: import.meta.env.VITE_COGNITO_REGION ?? "ap-northeast-1",
    // guide 用 Cognito App Client。CUSTOM_AUTH 有効（security-stack GuideAppClient）。
    clientId: requireInjected("VITE_COGNITO_CLIENT_ID", import.meta.env.VITE_COGNITO_CLIENT_ID),
  },
  // Active になったガイドを guide-app（稼働ガイドの日常利用）へ渡す橋のリンク先（tst-00cu.5）。
  // 割当の正本は infra/cdk/src/config/site-domains.ts（guide=guide-app）。値は config/frontend-env.json。
  // フォールバックは置かない（置くと scripts/check-frontend-bundle-env.mjs が落とす。それが正）。
  guideAppBaseUrl: requireInjected(
    "VITE_GUIDE_APP_BASE_URL",
    import.meta.env.VITE_GUIDE_APP_BASE_URL,
  ),
  // g-03「Contact TST support」導線 / g-01 応募問い合わせ先。
  supportEmail: import.meta.env.VITE_SUPPORT_EMAIL ?? "support@tokyosignaturetours.com",
} as const;

// 認証コード仕様（C-3-2 / 確定）。UI のカウントダウン・上限表示の単一の出所にする。
export const authCodePolicy = {
  expiresMinutes: 10, // コード有効期限
  resendIntervalSeconds: 60, // 再送可能になるまでの待機
  maxAttempts: 5, // 検証試行の上限
  codeLength: 6, // 6桁
} as const;

// 業務委託契約の表示メタ（g-02 Contract Signature の静的サマリ）。
// 条項本文は文書ストア未整備のため、主要条項のみ静的に提示する（GET /guides/me/contract も本文は返さない）。
//
// 版はここに持たない（tst-bak0.1.31）。以前は version: "2026.05" を持ち、それが
// consent_log.document_version へそのまま流れ込んでいたが、台帳（terms_version）の版と
// 一致する保証はどこにも無かった。版は GET /policies/guide_contract から取る
// （lib/policy-api.ts useGuideContractVersion）。
export const contractDoc = {
  title: "Guide Contractor Agreement",
  articleCount: 25,
  keyTerms: [
    "Monthly closing and next-month payment",
    "Guide-paid transport, admission, and meal expenses",
    "Direct dealing prohibited for 1 year",
    "Liability insurance required",
  ],
} as const;
